"use client";

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { toast } from "react-toastify";

import { FiMessageSquare, FiTrash2, FiCheckCircle } from "react-icons/fi";

import { UsersApi } from "../../App/api/users.api.js";
import { DeliveryManApis } from "../../App/api/delivery.apis.js";

import BasicTable from "../../Components/CommonTable/BasicTable/BasicTable.jsx";
import Button from "../../Components/Button.jsx";
import LoaderContext from "../../Components/loader/LoaderContext.js";

const TicketManagement = () => {
  const { handleLoading } = useContext(LoaderContext);
  const location = useLocation();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyDialog, setReplyDialog] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
  });

  // ================= USER ROLE FROM ROUTE =================

  const ticketRole = useMemo(() => {
    if (location.pathname.includes("customer-tickets")) return "customer";
    if (location.pathname.includes("vendor-tickets")) return "vendor";
    if (location.pathname.includes("deliveryman-tickets")) return "deliveryman";
    return "customer";
  }, [location.pathname]);

  // ================= FETCH TICKETS =================

  const fetchTickets = async () => {
    try {
      handleLoading(true);

      let res;

      if (ticketRole === "deliveryman") {
        res = await DeliveryManApis.getDeliveryManTickets({
          page: pagination.page,
          limit: pagination.limit,
        });

        const deliveryData = res?.data?.data;
        setTickets(Array.isArray(deliveryData) ? deliveryData : []);
        setPagination((prev) => ({
          ...prev,
          totalCount: Array.isArray(deliveryData) ? deliveryData.length : 0,
          totalPages: 1,
        }));
      } else {
        res = await UsersApi.getAllTickets({
          page: pagination.page,
          limit: pagination.limit,
          userRole: ticketRole,
          filter: {},
        });

        const responseData = res?.data?.data;
        setTickets(responseData?.tickets || []);
        setPagination((prev) => ({
          ...prev,
          totalCount: responseData?.totalCount || 0,
          totalPages: responseData?.totalPages || 0,
        }));
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch tickets");
    } finally {
      handleLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [pagination.page, ticketRole]);

  // ================= TABLE DATA =================

  const tableData =
    ticketRole === "deliveryman"
      ? tickets.map((item) => ({
          _id: item?._id,
          ticketNo: item?.ticketNo,
          title: item?.title,
          description: item?.description,
          status: item?.status,
          createdAt: item?.createdAt,
          originalData: item,
        }))
      : tickets;

  // ================= REPLY =================

  const handleReply = async () => {
    try {
      if (!replyMessage.trim()) {
        return toast.error("Reply message is required");
      }

      // ================= ROUTE TO CORRECT API =================

      if (ticketRole === "deliveryman") {
        await DeliveryManApis.replyToDeliveryManTicket(selectedTicket?._id, {
          message: replyMessage,
        });
      } else {
        await UsersApi.replyToTicket(selectedTicket?._id, {
          message: replyMessage,
        });
      }

      toast.success("Reply sent successfully");
      setReplyDialog(false);
      setReplyMessage("");
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to send reply");
    }
  };

  // ================= STATUS UPDATE =================

  const handleStatusUpdate = async (ticketId, status) => {
    try {
      // ================= ROUTE TO CORRECT API =================

      if (ticketRole === "deliveryman") {
        await DeliveryManApis.updateDeliveryManTicketStatus(ticketId, {
          status,
        });
      } else {
        await UsersApi.updateTicketStatus(ticketId, { status });
      }

      toast.success("Status updated");
      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to update status");
    }
  };

  // ================= DELETE =================

  const handleDelete = async (ticketId) => {
    try {
      await UsersApi.deleteTicket(ticketId);
      toast.success("Ticket deleted");
      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete ticket");
    }
  };

  // ================= STATUS STYLE =================

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return { background: "#fef3c7", color: "#b45309", border: "1px solid #fcd34d" };
      case "in_progress":
        return { background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd" };
      case "resolved":
        return { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" };
      case "closed":
        return { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };
      default:
        return { background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" };
    }
  };

  // ================= PAGE TITLE =================

  const pageTitle = useMemo(() => {
    switch (ticketRole) {
      case "vendor": return "Vendor Tickets";
      case "deliveryman": return "Deliveryman Tickets";
      default: return "Customer Tickets";
    }
  }, [ticketRole]);

  // ================= DELIVERYMAN COLUMNS =================

  const deliveryManColumns = [
    { field: "ticketNo", header: "Ticket No" },
    { field: "title", header: "Title" },
    {
      field: "description",
      header: "Description",
      accessor: (rowData) => (
        <div className="max-w-[300px] truncate text-sm text-gray-700">
          {rowData?.description}
        </div>
      ),
    },
    {
      field: "status",
      header: "Status",
      accessor: (rowData) => (
        <Tag
          value={rowData?.status}
          style={{
            ...getStatusStyle(rowData?.status),
            padding: "6px 14px",
            borderRadius: "999px",
            fontWeight: "600",
            fontSize: "12px",
          }}
        />
      ),
    },
    {
      field: "createdAt",
      header: "Created At",
      accessor: (rowData) => new Date(rowData?.createdAt).toLocaleDateString(),
    },
    {
      field: "actions",
      header: "Actions",
      accessor: (rowData) => (
        <div className="flex items-center gap-2">

          {/* Reply */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTicket(rowData);
              setReplyDialog(true);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-all hover:bg-blue-200"
          >
            <FiMessageSquare size={16} />
          </button>

          {/* Resolve */}
          {rowData?.status !== "resolved" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStatusUpdate(rowData?._id, "resolved");
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 text-green-600 transition-all hover:bg-green-200"
            >
              <FiCheckCircle size={16} />
            </button>
          )}

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(rowData?._id);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 text-green-600 transition-all hover:bg-green-200"
          >
            <FiTrash2 size={16} />
          </button>

        </div>
      ),
    },
  ];

  // ================= CUSTOMER / VENDOR COLUMNS =================

  const columns = [
    { field: "ticket_id", header: "Ticket ID" },
    {
      field: "customer",
      header: "User",
      accessor: (rowData) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-800">
            {rowData?.created_by?.first_name} {rowData?.created_by?.last_name}
          </span>
          <span className="text-xs text-gray-500">{rowData?.created_by?.email}</span>
        </div>
      ),
    },
    {
      field: "userRole",
      header: "User Role",
      accessor: (rowData) => (
        <span className="font-semibold capitalize text-gray-800">
          {rowData?.created_by?.userRole?.name}
        </span>
      ),
    },
    { field: "problem_type", header: "Problem Type" },
    { field: "subject", header: "Subject" },
    {
      field: "description",
      header: "Complaint",
      accessor: (rowData) => (
        <div className="max-w-[250px] truncate text-sm text-gray-700">
          {rowData?.description}
        </div>
      ),
    },
    {
      field: "priority",
      header: "Priority",
      accessor: (rowData) => (
        <Tag
          value={rowData?.priority}
          severity={
            rowData?.priority === "URGENT" ? "danger"
            : rowData?.priority === "HIGH" ? "warning"
            : rowData?.priority === "MEDIUM" ? "info"
            : "success"
          }
        />
      ),
    },
    {
      field: "status",
      header: "Status",
      accessor: (rowData) => (
        <Tag
          value={rowData?.status}
          style={{
            ...getStatusStyle(rowData?.status),
            padding: "6px 14px",
            borderRadius: "999px",
            fontWeight: "600",
            fontSize: "12px",
          }}
        />
      ),
    },
    {
      field: "createdAt",
      header: "Created At",
      accessor: (rowData) => new Date(rowData?.createdAt).toLocaleDateString(),
    },
    {
      field: "actions",
      header: "Actions",
      accessor: (rowData) => (
        <div className="flex items-center gap-2">

          {/* Reply */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTicket(rowData);
              setReplyDialog(true);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-all hover:bg-blue-200"
          >
            <FiMessageSquare size={16} />
          </button>

          {/* Resolve */}
          {rowData?.status !== "RESOLVED" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStatusUpdate(rowData?._id, "RESOLVED");
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 text-green-600 transition-all hover:bg-green-200"
            >
              <FiCheckCircle size={16} />
            </button>
          )}

          {/* Delete */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(rowData?._id);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100 text-green-600 transition-all hover:bg-green-200"
          >
            <FiTrash2 size={16} />
          </button>

        </div>
      ),
    },
  ];

  // ================= FINAL COLUMNS =================

  const finalColumns = ticketRole === "deliveryman" ? deliveryManColumns : columns;

  // ================= REPLY MODAL: FIELD HELPERS =================
  // Deliveryman tickets use different field names than customer/vendor tickets

  const selectedTicketId =
    ticketRole === "deliveryman"
      ? selectedTicket?.ticketNo
      : selectedTicket?.ticket_id;

  const selectedTicketUser =
    ticketRole === "deliveryman"
      ? null // deliveryman tickets have no created_by
      : `${selectedTicket?.created_by?.first_name ?? ""} ${selectedTicket?.created_by?.last_name ?? ""}`.trim();

  const selectedTicketEmail =
    ticketRole === "deliveryman"
      ? null
      : selectedTicket?.created_by?.email;

  const selectedTicketSubject =
    ticketRole === "deliveryman"
      ? selectedTicket?.title
      : selectedTicket?.subject;

  return (
    <div className="p-5">

      {/* ================= HEADER ================= */}
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-gray-900">{pageTitle}</h2>
        <p className="mt-1 text-sm text-gray-500">
          Manage support tickets and customer complaints
        </p>
      </div>

      {/* ================= TABLE ================= */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <BasicTable
          columns={finalColumns}
          data={tableData}
          paginatorData={pagination}
          loading={loading}
          onPaginatorClick={(pageData) => {
            setPagination((prev) => ({ ...prev, page: pageData?.page + 1 }));
          }}
        />
      </div>

      {/* ================= REPLY MODAL ================= */}
      <Dialog
        visible={replyDialog}
        onHide={() => { setReplyDialog(false); setReplyMessage(""); }}
        className="ticket-reply-dialog"
        style={{ width: "700px" }}
        draggable={false}
        resizable={false}
        showHeader={false}
        modal
        dismissableMask
        maskClassName="backdrop-blur-md bg-black/60"
      >
        <div className="relative overflow-hidden rounded-3xl bg-white">

          {/* HEADER */}
          <div className="sticky top-0 z-20 bg-green-500 p-6 text-white shadow-md">
            <button
              onClick={() => { setReplyDialog(false); setReplyMessage(""); }}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white transition-all hover:bg-white/30"
            >
              ✕
            </button>
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                <FiMessageSquare size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Reply To Ticket</h3>
                <p className="mt-1 text-sm text-green-100">
                  Respond to{" "}
                  {ticketRole === "deliveryman" ? "delivery man" : "customer"}{" "}
                  complaint professionally
                </p>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div
            className="overflow-y-auto p-6"
            style={{ maxHeight: "75vh", scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`
              .ticket-reply-dialog .p-dialog-content::-webkit-scrollbar,
              .ticket-reply-dialog .overflow-y-auto::-webkit-scrollbar { width: 0px; display: none; }
              .ticket-reply-dialog .p-dialog-content { padding: 0 !important; border-radius: 24px; overflow: hidden; }
            `}</style>

            {/* TICKET CARD */}
            <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <p className="mb-1 text-xs text-gray-500">Ticket ID</p>
                  <h4 className="text-lg font-bold text-gray-800">
                    {selectedTicketId}
                  </h4>
                </div>
                <Tag
                  value={selectedTicket?.status}
                  style={{
                    ...getStatusStyle(selectedTicket?.status),
                    padding: "6px 14px",
                    borderRadius: "999px",
                    fontWeight: "600",
                    fontSize: "12px",
                  }}
                />
              </div>

              {/* User — only for customer/vendor */}
              {selectedTicketUser && (
                <div className="mb-4">
                  <p className="mb-1 text-xs text-gray-500">User</p>
                  <h4 className="font-semibold text-gray-800">{selectedTicketUser}</h4>
                  <span className="text-sm text-gray-500">{selectedTicketEmail}</span>
                </div>
              )}

              <div className="mb-4">
                <p className="mb-1 text-xs text-gray-500">Subject</p>
                <h4 className="font-semibold text-gray-800">{selectedTicketSubject}</h4>
              </div>

              <div>
                <p className="mb-2 text-xs text-gray-500">Complaint</p>
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm leading-6 text-gray-700">
                  {selectedTicket?.description}
                </div>
              </div>
            </div>

            {/* REPLY BOX */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800">
                Your Reply
              </label>
              <InputTextarea
                rows={6}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Write a professional response..."
                className="w-full rounded-2xl border border-gray-300 p-4 text-sm focus:ring-2 focus:ring-blue-500"
                style={{ resize: "none" }}
              />
            </div>

            {/* BUTTONS */}
            <div className="mt-6 flex justify-end gap-3">
              <Button
                text="Cancel"
                outlined
                onClick={() => { setReplyDialog(false); setReplyMessage(""); }}
              />
              <Button text="Send Reply" onClick={handleReply} />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TicketManagement;